import { useFormControlContext } from "@/components/Context/FormControl";
import Icon from "@/components/Ui/Icon";
import Spinner from "@/components/Ui/Spinner";
import { IconName } from "icons/types";
import { ChangeEvent, FocusEvent, useState } from "react";
import { twMerge } from "tailwind-merge";

type TextInputProps = {
  value?: string;
  id?: string;
  type?: "text" | "tel" | "search" | "password" | "email";
  hasError?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  name?: string;
  readOnly?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  tabIndex?: number;
  pattern?: string;
  required?: boolean;
  compact?: boolean;
  slim?: boolean;
  inputMode?: "text" | "numeric";
  icon?: IconName;
  isLoading?: boolean;
  autoComplete?: string;
  inputClassName?: string;
};

const TextInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  id,
  type = "text",
  hasError,
  placeholder,
  name,
  readOnly,
  disabled,
  autoFocus,
  tabIndex,
  pattern = ".*",
  required,
  compact = false,
  slim = false,
  inputMode,
  icon,
  isLoading,
  autoComplete,
  inputClassName,
}: TextInputProps) => {
  const formContext = useFormControlContext();
  const [localValue, setLocalValue] = useState(value);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
    onChange(event);
  };

  return (
    <div
      className={twMerge(
        "relative w-full font-normal",
        disabled && "opacity-40",
      )}
    >
      {!isLoading && icon && (
        <Icon
          className={twMerge(
            "text-muted-foreground absolute top-1/2 left-5 -translate-y-1/2",
            slim && "left-3",
          )}
          name={icon}
        />
      )}
      {isLoading && (
        <div
          className={twMerge(
            "absolute top-1/2 left-5 flex -translate-y-1/2",
            slim && "left-3",
          )}
        >
          <Spinner />
        </div>
      )}
      <input
        className={twMerge(
          "peer border-border bg-input/30 placeholder:text-muted-foreground read-only:border-border! read-only:bg-input! hover:bg-interact-input focus:border-interact-border focus:bg-interact-input w-full rounded border p-5 text-base text-white outline-0 transition-colors duration-150 placeholder:text-base disabled:cursor-not-allowed sm:text-base",
          hasError && "border-destructive focus:border-destructive",
          compact && "px-5 pt-7 pb-3",
          slim && "border-none px-5 py-2.5",
          icon && "pl-[52px]",
          slim && icon && "pl-[42px]",
          inputClassName,
        )}
        type={type}
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        id={id || formContext.id}
        name={name}
        readOnly={readOnly}
        disabled={disabled}
        pattern={pattern}
        required={required}
        placeholder={!compact ? placeholder : undefined}
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        inputMode={inputMode}
        autoComplete={autoComplete}
      />
      {compact && (
        <label
          htmlFor={id || formContext.id}
          className={twMerge(
            "text-muted-foreground pointer-events-none absolute top-1/2 right-5 left-5 -translate-y-1/2 overflow-hidden text-base whitespace-nowrap transition-all peer-focus:-translate-y-[calc(50%+theme(space.3))] peer-focus:text-xs sm:text-base",
            (value !== undefined ? value : localValue) &&
              "-translate-y-[calc(50%+theme(space.3))] text-xs sm:text-xs",
          )}
        >
          {placeholder}
        </label>
      )}
    </div>
  );
};

export default TextInput;
